package nl.staartvin.solarpower.web

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import nl.staartvin.solarpower.data.UvRepository
import nl.staartvin.solarpower.data.chart.UvChartData
import nl.staartvin.solarpower.data.chart.UvSeverity
import nl.staartvin.solarpower.data.currentuvindex.CurrentUvIndexClient
import nl.staartvin.solarpower.data.exposure.DoseState
import nl.staartvin.solarpower.data.exposure.minutesLeftAtUvi
import nl.staartvin.solarpower.data.model.Location
import nl.staartvin.solarpower.data.model.UvDay
import nl.staartvin.solarpower.data.rivm.RivmWmsClient
import nl.staartvin.solarpower.data.uv.SkinType
import nl.staartvin.solarpower.data.uv.SpfLevel
import nl.staartvin.solarpower.data.uv.formatSafeDuration
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.events.Event

/**
 * Web PWA entry point. Mirrors the app's compact experience: locate the user,
 * fetch UV via the SHARED Kotlin (same `UvRepository`/`UvChartData` as Android
 * and iOS), and render an interactive SVG chart with a safe-time readout.
 *
 * No backend: the browser does the calls and all the maths. The only server
 * involvement is Nginx Proxy Manager forwarding the two `/up/...` paths to the
 * upstreams (browser CORS) and serving these static files over HTTPS.
 */

private val scope = MainScope()

// Utrecht — same fallback the native apps use when there's no location fix.
private val FALLBACK = Location(latitude = 52.10, longitude = 5.18)

private const val PREF_SKIN = "skin_type"
private const val PREF_SPF = "spf_factor"
private const val PREF_LOC_MODE = "location_mode" // "on" | "off" | absent (ask)

private var skinType: SkinType = SkinType.Default
private var spf: SpfLevel = SpfLevel.Default
private var dayOffset: Int = 0            // 0 = today, 1 = tomorrow
private var loadedDay: UvDay? = null
private var usedFallback: Boolean = false
private var coordsLabel: String = ""
private var placeName: String? = null     // reverse-geocoded town, filled in async
private var handleFraction: Double = 0.5

// Chart is drawn at the container's real pixel size (no viewBox stretching),
// re-measured on resize. These hold the current chart + its pixel dimensions
// so scrub updates and resize redraws share one source of truth.
private var currentChart: UvChartData? = null
private var vbW: Double = 0.0
private var vbH: Double = 0.0
private var chartObserver: ResizeObserver? = null

/** Minimal external binding for the browser's ResizeObserver. */
private external class ResizeObserver(callback: (dynamic, dynamic) -> Unit) {
    fun observe(target: Element)
    fun disconnect()
}

fun main() {
    // Restore persisted preferences (localStorage is the web's SharedPreferences).
    window.localStorage.getItem(PREF_SKIN)?.let { skinType = SkinType.fromName(it) }
    window.localStorage.getItem(PREF_SPF)?.toDoubleOrNull()?.let { spf = SpfLevel.fromFactor(it) }

    // First visit shows the explainer; after that we honour the saved choice.
    // The user can flip it any time via the settings panel.
    when (window.localStorage.getItem(PREF_LOC_MODE)) {
        "on" -> startLocating()
        "off" -> proceed(FALLBACK, isFallback = true)
        else -> showIntro()
    }
}

/**
 * Priming screen: tells the user why we need their location and asks before
 * the browser's own permission prompt. Firing the real request from this
 * button (a user gesture) is also friendlier to the browser than an on-load
 * request.
 */
private fun showIntro() {
    val root = document.getElementById("app") as HTMLElement
    root.innerHTML = ""
    val box = div("intro")
    box.appendChild(el("h1", "intro-title", "Solarpower"))
    box.appendChild(el("p", "intro-text",
        "See how strong the sun is where you are — and how long you can safely be in it."))
    box.appendChild(el("p", "intro-text",
        "To do that we need your location. It's read once, in your browser, only to fetch " +
            "the UV forecast for your spot — it isn't stored or sent anywhere else."))

    val btn = document.createElement("button") as HTMLElement
    btn.className = "intro-btn"
    btn.textContent = "I'm okay with sharing my location"
    btn.addEventListener("click", {
        window.localStorage.setItem(PREF_LOC_MODE, "on")
        startLocating()
    })
    box.appendChild(btn)

    val skip = document.createElement("button") as HTMLElement
    skip.className = "intro-skip"
    skip.textContent = "Continue without sharing"
    skip.addEventListener("click", {
        window.localStorage.setItem(PREF_LOC_MODE, "off")
        proceed(FALLBACK, isFallback = true)
    })
    box.appendChild(skip)

    root.appendChild(box)
}

/** Trigger the browser geolocation prompt, then load. */
private fun startLocating() {
    setStatus("Finding your location…")
    locate { location, isFallback -> proceed(location, isFallback) }
}

/** Resolve the place name (async, non-blocking) and fetch + render the UV data. */
private fun proceed(location: Location, isFallback: Boolean) {
    usedFallback = isFallback
    coordsLabel = "${format(location.latitude, 3)}, ${format(location.longitude, 3)}"

    // Place name in parallel — cosmetic, so it must never delay the UV fetch.
    if (!isFallback) {
        scope.launch {
            placeName = reverseGeocode(location.latitude, location.longitude)
            updateLocationLine()
        }
    }

    scope.launch {
        try {
            setStatus("Fetching today's UV…")
            loadedDay = repository().fetch(location, countryCode = null)
            render()
        } catch (e: Throwable) {
            setStatus("Couldn't load UV data. Reload to try again.")
            console.error("UV fetch failed", e)
        }
    }
}

/**
 * Reverse-geocode a coordinate to a town name via OpenStreetMap Nominatim.
 * Browser-side (Nominatim allows CORS); result cached in localStorage per
 * rounded coordinate to stay a polite, low-volume client. Cosmetic only —
 * returns null on any failure and the UI falls back to coordinates.
 */
private suspend fun reverseGeocode(lat: Double, lon: Double): String? {
    val key = "geo_${format(lat, 3)}_${format(lon, 3)}"
    window.localStorage.getItem(key)?.let { return it.ifEmpty { null } }
    return try {
        val url = "https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10" +
            "&lat=$lat&lon=$lon"
        val resp = window.fetch(url).await()
        if (!resp.ok) return null
        val data: dynamic = resp.json().await()
        val a = data.address
        val name = (a?.city ?: a?.town ?: a?.village ?: a?.municipality ?: a?.suburb
            ?: a?.county ?: data.name) as? String
        val clean = name?.takeIf { it.isNotBlank() }
        // Cache even a null result (as "") so we don't re-query a dead spot.
        window.localStorage.setItem(key, clean ?: "")
        clean
    } catch (e: Throwable) {
        null
    }
}

/** Location line text: place name if resolved, else fallback marker or coords. */
private fun locationLineText(): String = when {
    usedFallback -> "Utrecht · no location fix"
    placeName != null -> placeName!!
    else -> coordsLabel
}

/** Patch the location line in place when the async place name arrives. */
private fun updateLocationLine() {
    (document.getElementById("loc-line") as? HTMLElement)?.textContent = locationLineText()
}

/** Repository pointed at same-origin proxy paths so the browser's CORS is satisfied. */
private fun repository(): UvRepository {
    val origin = window.location.origin
    val cuvi = "$origin/up/cuvi/api/v1/uvi"
    val rivm = "$origin/up/rivm/geo/alo/wms"
    return UvRepository.create(cuvi, rivm)
}

// ---- Location -------------------------------------------------------------

private fun locate(onResult: (Location, isFallback: Boolean) -> Unit) {
    val geo = window.navigator.asDynamic().geolocation
    if (geo == null || geo == undefined) {
        onResult(FALLBACK, true)
        return
    }
    // Dynamic access: the Geolocation API isn't in the DOM externals.
    geo.getCurrentPosition(
        { position: dynamic ->
            val lat = position.coords.latitude as Double
            val lon = position.coords.longitude as Double
            onResult(Location(lat, lon), false)
        },
        { _: dynamic -> onResult(FALLBACK, true) },
        js("({ enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 })"),
    )
}

// ---- Rendering ------------------------------------------------------------

private fun render() {
    val day = loadedDay ?: return
    val chart = UvChartData.forRelativeDay(day, dayOffset)
    currentChart = chart

    // Default the scrub to the current clock time (interpolated) when it's in
    // view (Today), else the chart midpoint (Tomorrow).
    handleFraction = chart.currentPoint?.let {
        chart.normalizeTimeMillis(it.time.toEpochMilliseconds())
    } ?: 0.5

    // Headline "now" = today's current interpolated UVI, regardless of tab.
    val todayChart = if (dayOffset == 0) chart else UvChartData.forRelativeDay(day, 0)
    val currentUvi = todayChart.currentPoint?.uvi ?: day.now.uvi

    val root = document.getElementById("app") as HTMLElement
    root.innerHTML = ""

    // Text blocks are padded; the chart sits between them and spans the full
    // width (edge-to-edge on phones, card-width on larger screens).
    val top = div("block")
    top.appendChild(header(day, currentUvi))
    top.appendChild(controls())
    root.appendChild(top)

    val wrap = div("chart-wrap").also { it.id = "chart-wrap" }
    root.appendChild(wrap)

    val bottom = div("block")
    bottom.appendChild(readoutLine(chart))
    bottom.appendChild(el("div", "legend", "Solid = measured · Dashed = forecast"))
    root.appendChild(bottom)

    // A ResizeObserver draws the chart as soon as the container has a real
    // size and redraws on every resize — robust to first-measure-is-zero
    // (background tab, pre-layout) and to viewport changes, all in one.
    observeChart(wrap)
}

private fun observeChart(wrap: Element) {
    val existing = chartObserver
    if (existing != null) {
        existing.disconnect()
        existing.observe(wrap)
    } else {
        // Defer the redraw to the next frame. The chart container is now
        // flex-sized, so mutating it synchronously inside the observer callback
        // makes the browser log "ResizeObserver loop completed with undelivered
        // notifications". A requestAnimationFrame hop breaks that same-frame loop.
        val ro = ResizeObserver({ _, _ -> window.requestAnimationFrame { drawChart() } })
        ro.observe(wrap)
        chartObserver = ro
    }
}

private fun header(day: UvDay, currentUvi: Double): Element {
    val sev = UvSeverity.forUvi(currentUvi)
    val box = div("header")

    val col = div("header-text")
    col.appendChild(el("div", "severity", sev.displayName))
    col.appendChild(el("div", "sub", "UVI ${format(currentUvi, 1)} · ${skinType.shortName} skin"))
    col.appendChild(el("div", "sub", locationLineText()).also { it.id = "loc-line" })
    box.appendChild(col)

    val gear = document.createElement("button") as HTMLElement
    gear.className = "gear"
    gear.setAttribute("aria-label", "Settings")
    gear.textContent = "⚙"
    gear.addEventListener("click", { showSettings() })
    box.appendChild(gear)

    return box
}

/**
 * Settings panel — currently just location control. A web page can't revoke
 * the browser's geolocation permission itself, so this governs whether the
 * app *uses* location, and points the user to browser settings for a full
 * revoke.
 */
private fun showSettings() {
    val overlay = div("overlay")
    val panel = div("panel")

    panel.appendChild(el("h2", "panel-title", "Location"))
    panel.appendChild(el("p", "panel-text",
        if (usedFallback) "Solarpower isn't using your location — it's showing Utrecht."
        else "Solarpower is using your location to show UV for your area."))

    val toggle = document.createElement("button") as HTMLElement
    toggle.className = "panel-btn"
    if (usedFallback) {
        toggle.textContent = "Use my location"
        toggle.addEventListener("click", {
            window.localStorage.setItem(PREF_LOC_MODE, "on")
            closeOverlay(overlay)
            startLocating()
        })
    } else {
        toggle.textContent = "Stop using my location"
        toggle.addEventListener("click", {
            window.localStorage.setItem(PREF_LOC_MODE, "off")
            placeName = null
            closeOverlay(overlay)
            proceed(FALLBACK, isFallback = true)
        })
    }
    panel.appendChild(toggle)

    panel.appendChild(el("p", "panel-note",
        "This controls whether the app asks for your location. To fully block " +
            "location for this site, use your browser's settings for this page."))

    val close = document.createElement("button") as HTMLElement
    close.className = "panel-close"
    close.textContent = "Close"
    close.addEventListener("click", { closeOverlay(overlay) })
    panel.appendChild(close)

    overlay.appendChild(panel)
    // Backdrop click closes; clicks on the panel itself don't bubble out.
    overlay.addEventListener("click", { ev -> if (ev.target == overlay) closeOverlay(overlay) })
    document.body?.appendChild(overlay)
}

private fun closeOverlay(overlay: Element) {
    overlay.parentElement?.removeChild(overlay)
}

private fun controls(): Element {
    val box = div("controls")

    // Today / Tomorrow toggle.
    val toggle = div("toggle")
    listOf(0 to "Today", 1 to "Tomorrow").forEach { (offset, label) ->
        val b = document.createElement("button") as HTMLElement
        b.textContent = label
        b.className = if (offset == dayOffset) "tab active" else "tab"
        b.addEventListener("click", {
            dayOffset = offset
            render()   // render() sets the default scrub position per tab
        })
        toggle.appendChild(b)
    }
    box.appendChild(toggle)

    // Skin type + SPF selectors.
    box.appendChild(
        selectField("Skin", SkinType.all.map { it.name to "${it.romanNumeral} · ${it.shortName}" }, skinType.name) { picked ->
            skinType = SkinType.fromName(picked)
            window.localStorage.setItem(PREF_SKIN, skinType.name)
            render()
        },
    )
    box.appendChild(
        selectField("Sunscreen", SpfLevel.all.map { it.factor.toString() to it.label }, spf.factor.toString()) { picked ->
            spf = SpfLevel.fromFactor(picked.toDouble())
            window.localStorage.setItem(PREF_SPF, spf.factor.toString())
            render()
        },
    )
    return box
}

private const val SVG_NS = "http://www.w3.org/2000/svg"
private const val PAD_L = 10.0
private const val PAD_R = 10.0
private const val PAD_T = 14.0
private const val PAD_B = 24.0

/** Measure the container and draw the SVG at its real pixel size (no stretching). */
private fun drawChart() {
    val chart = currentChart ?: return
    val wrap = document.getElementById("chart-wrap") as? HTMLElement ?: return
    val w = wrap.clientWidth.toDouble()
    val h = wrap.clientHeight.toDouble()
    if (w < 2 || h < 2) return
    // Skip if nothing actually changed and the chart is already drawn — avoids
    // redundant SVG rebuilds when the observer fires with an unchanged size.
    if (w == vbW && h == vbH && wrap.hasChildNodes()) return
    vbW = w
    vbH = h
    wrap.innerHTML = ""
    wrap.appendChild(buildSvg(chart, w, h))
}

private fun buildSvg(chart: UvChartData, w: Double, h: Double): Element {
    val svg = document.createElementNS(SVG_NS, "svg")
    // viewBox matches the pixel size 1:1, so strokes stay crisp and nothing
    // is distorted regardless of the container's aspect ratio.
    svg.setAttribute("viewBox", "0 0 $w $h")
    svg.setAttribute("width", "100%")
    svg.setAttribute("height", "100%")
    svg.setAttribute("class", "chart")

    val cw = w - PAD_L - PAD_R
    val ch = h - PAD_T - PAD_B
    fun x(ms: Long) = PAD_L + chart.normalizeTimeMillis(ms) * cw
    fun y(uvi: Double) = PAD_T + (1.0 - chart.normalizeUvi(uvi)) * ch

    // Hour gridlines + labels (every other hour).
    chart.hourMarkers().forEach { m ->
        val gx = x(m.timeMillis)
        svg.appendChild(line(gx, PAD_T, gx, PAD_T + ch, "grid"))
        if (m.hour % 2 == 0) svg.appendChild(text(gx, h - 7, "${m.hour}", "axis"))
    }

    // Severity-coloured curve: one segment per sample pair, coloured by the
    // severity at its midpoint (same as the mobile apps). Segments at/after
    // the measured cutoff are forecast → dashed.
    val samples = chart.allSamples
    val cutoff = chart.measuredCutoffMillis
    for (i in 0 until samples.size - 1) {
        val a = samples[i]
        val b = samples[i + 1]
        val sev = UvSeverity.forUvi((a.uvi + b.uvi) / 2.0)
        val seg = line(
            x(a.time.toEpochMilliseconds()), y(a.uvi),
            x(b.time.toEpochMilliseconds()), y(b.uvi),
            "seg",
        )
        seg.setAttribute("stroke", "rgb(${sev.r}, ${sev.g}, ${sev.b})")
        if (a.time.toEpochMilliseconds() >= cutoff) seg.setAttribute("stroke-dasharray", "6 5")
        svg.appendChild(seg)
    }

    // "Now" marker — the current clock time (interpolated); null on Tomorrow.
    chart.currentPoint?.let { now ->
        val nx = x(now.time.toEpochMilliseconds())
        svg.appendChild(line(nx, PAD_T, nx, PAD_T + ch, "now"))
        svg.appendChild(circle(nx, y(now.uvi), 4.0, "now-dot"))
    }

    // Scrub handle.
    chart.readoutAtFraction(handleFraction)?.let { r ->
        val hx = x(r.timeMillis)
        svg.appendChild(line(hx, PAD_T, hx, PAD_T + ch, "handle").also { it.id = "scrub-line" })
        svg.appendChild(circle(hx, y(r.uvi), 6.0, "handle-dot").also { it.id = "scrub-dot" })
    }

    // Pointer scrub: translate clientX → fraction and update in place.
    val onMove: (Event) -> Unit = { ev ->
        val rect = svg.asDynamic().getBoundingClientRect()
        val clientX = (ev.asDynamic().clientX as? Double)
            ?: (ev.asDynamic().touches?.get(0)?.clientX as? Double)
        if (clientX != null) {
            val fracX = (clientX - (rect.left as Double)) / (rect.width as Double)
            val frac = ((fracX * vbW - PAD_L) / (vbW - PAD_L - PAD_R)).coerceIn(0.0, 1.0)
            handleFraction = frac
            updateScrub(chart)
        }
    }
    svg.addEventListener("pointerdown", onMove)
    svg.addEventListener("pointermove", { ev ->
        if ((ev.asDynamic().buttons as? Int ?: 0) != 0) onMove(ev)
    })
    return svg
}

/** Cheap in-place update while scrubbing: move the handle + refresh the readout text. */
private fun updateScrub(chart: UvChartData) {
    val cw = vbW - PAD_L - PAD_R
    val ch = vbH - PAD_T - PAD_B
    val readout = chart.readoutAtFraction(handleFraction) ?: return
    val hx = PAD_L + chart.normalizeTimeMillis(readout.timeMillis) * cw
    val hy = PAD_T + (1.0 - chart.normalizeUvi(readout.uvi)) * ch
    (document.getElementById("scrub-line") as? Element)?.apply {
        setAttribute("x1", "$hx"); setAttribute("x2", "$hx")
    }
    (document.getElementById("scrub-dot") as? Element)?.apply {
        setAttribute("cx", "$hx"); setAttribute("cy", "$hy")
    }
    (document.getElementById("readout") as? HTMLElement)?.textContent = readoutText(readout.timeLabel, readout.uvi)
}

private fun readoutLine(chart: UvChartData): Element {
    val readout = chart.readoutAtFraction(handleFraction)
    val t = if (readout != null) readoutText(readout.timeLabel, readout.uvi) else "—"
    return el("div", "readout", t).also { it.id = "readout" }
}

private fun readoutText(timeLabel: String, uvi: Double): String {
    val left = minutesLeftAtUvi(DoseState.Empty, uvi, skinType, spf.factor)
    val tail = if (left == null) "" else {
        val dur = formatSafeDuration(left)
        if (spf.isNone) " · $dur bare skin" else " · $dur with ${spf.label}"
    }
    return "$timeLabel · UVI ${format(uvi, 1)}$tail"
}

// ---- Tiny DOM helpers -----------------------------------------------------

private fun setStatus(message: String) {
    (document.getElementById("app") as? HTMLElement)?.innerHTML =
        "<div class=\"status\">$message</div>"
}

private fun div(cls: String): HTMLElement =
    (document.createElement("div") as HTMLElement).also { it.className = cls }

private fun el(tag: String, cls: String, txt: String): HTMLElement =
    (document.createElement(tag) as HTMLElement).also { it.className = cls; it.textContent = txt }

private fun selectField(
    label: String,
    options: List<Pair<String, String>>,
    selected: String,
    onChange: (String) -> Unit,
): Element {
    val wrap = div("field")
    wrap.appendChild(el("label", "field-label", label))
    val sel = document.createElement("select") as HTMLSelectElement
    options.forEach { (value, text) ->
        val opt = document.createElement("option") as HTMLElement
        opt.setAttribute("value", value)
        opt.textContent = text
        if (value == selected) opt.setAttribute("selected", "true")
        sel.appendChild(opt)
    }
    sel.addEventListener("change", { onChange(sel.value) })
    wrap.appendChild(sel)
    return wrap
}

private fun line(x1: Double, y1: Double, x2: Double, y2: Double, cls: String): Element =
    document.createElementNS(SVG_NS, "line").also {
        it.setAttribute("x1", "$x1"); it.setAttribute("y1", "$y1")
        it.setAttribute("x2", "$x2"); it.setAttribute("y2", "$y2")
        it.setAttribute("class", cls)
    }

private fun circle(cx: Double, cy: Double, r: Double, cls: String): Element =
    document.createElementNS(SVG_NS, "circle").also {
        it.setAttribute("cx", "$cx"); it.setAttribute("cy", "$cy")
        it.setAttribute("r", "$r"); it.setAttribute("class", cls)
    }

private fun text(x: Double, y: Double, txt: String, cls: String): Element =
    document.createElementNS(SVG_NS, "text").also {
        it.setAttribute("x", "$x"); it.setAttribute("y", "$y")
        it.setAttribute("class", cls); it.setAttribute("text-anchor", "middle")
        it.textContent = txt
    }

/** Locale-independent fixed-decimal formatting (no String.format on JS). */
private fun format(value: Double, decimals: Int): String = value.asDynamic().toFixed(decimals) as String
